/**
 * Locale-aware formatting, shared rather than duplicated per screen — a
 * kickoff time and a point total should read the same way (weekday names,
 * digit grouping) wherever they're shown. PLAN.md Phase 7.
 */

/** A kickoff time as "Sat, Aug 8, 7:00 PM" in `en`, or the pt-BR equivalent. */
export function formatKickoff(kickoffAt: string | null, locale: string): string | null {
  if (!kickoffAt) return null;
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(kickoffAt));
}

/**
 * A kickoff/deadline as two short display strings — "15/08/26" and "19:30"
 * — for the two-line date block a match/question card shows instead of one
 * long single-line string. Always 24-hour time (`hourCycle: 'h23'`)
 * regardless of locale: a sports fixture list reads clock times the same
 * way everywhere, unlike a kickoff's weekday/month names in `formatKickoff`
 * above, which do vary by locale.
 */
export function formatKickoffParts(
  kickoffAt: string | null,
  locale: string,
): { date: string; time: string } | null {
  if (!kickoffAt) return null;
  const d = new Date(kickoffAt);
  return {
    date: new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(d),
    time: new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(d),
  };
}

/** A count, grouped the way the locale expects (thousands separators etc.). */
export function formatNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * A bare `YYYY-MM-DD` as a real `Date` at LOCAL midnight.
 *
 * `new Date('2027-05-20')` would parse as midnight *UTC*, which then renders as
 * the 19th anywhere west of Greenwich — so a `date` question's guess would show
 * a different day than the one that was tapped. Building it from components
 * sidesteps that entirely: these values are calendar days, not instants (see
 * `question_predictions.dateGuess`), and this is the only correct way to hand
 * one to `Intl`.
 */
export function parseCalendarDay(day: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return null;
  const [, year, month, date] = match;
  return new Date(Number(year), Number(month) - 1, Number(date));
}

/** A `YYYY-MM-DD` calendar day as e.g. "May 20" / "20 de mai." — never shifted by the reader's time zone. */
export function formatCalendarDay(day: string | null, locale: string): string | null {
  const parsed = day ? parseCalendarDay(day) : null;
  if (!parsed) return null;
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(parsed);
}

/**
 * The hour a date vote counts as. Mirrors `GUESS_HOUR` in the API's
 * `date-scoring.ts`, which is where the rule is actually applied — this
 * constant only ever renders it.
 */
const GUESS_HOUR = 12;

/**
 * A guess as the moment it will actually be scored against: "May 20, 12:00",
 * or with `style: 'long'`, "20 May 2026, 12:00".
 *
 * A date question never asks anyone for a time — the day is the whole answer —
 * but the noon it silently counts as decides who wins by hours, so showing it
 * beats leaving people to discover the rule from the results. The long form
 * carries the year, since a pool guessing at a late-December birth from
 * November spans two of them.
 */
export function formatGuessAtNoon(
  day: string | null,
  locale: string,
  style: 'short' | 'long' = 'short',
): string | null {
  const parsed = day ? parseCalendarDay(day) : null;
  if (!parsed) return null;

  parsed.setHours(GUESS_HOUR, 0, 0, 0);
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: style === 'long' ? 'long' : 'short',
    ...(style === 'long' ? { year: 'numeric' as const } : {}),
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(parsed);
}

/**
 * A wall-clock `YYYY-MM-DD[ T]HH:MM[:SS]` as e.g. "May 20, 15:34".
 *
 * Read literally, same as `parseCalendarDay` and for the same reason: a `date`
 * question's real moment is a fact about where the event happened, not an
 * instant to re-project into the reader's zone (see `questions.realDateAt`).
 */
export function formatWallClock(value: string | null, locale: string): string | null {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(value) : null;
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(parsed);
}

/**
 * A gap in minutes as "3h34m" — total hours, so a two-day miss reads "44h26m"
 * rather than "1d 20h".
 *
 * Deliberately not locale-formatted: it's a stopwatch-style figure in a
 * standings column, and the design it comes from shows exactly this shape. The
 * unit letters are the same in both supported locales.
 */
export function formatDeltaMinutes(minutes: number): string {
  return `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, '0')}m`;
}

/** An ISO datetime split into the separate `<input type="date">`/`<input type="time">` values that edit it — local time, not UTC, since that's what those inputs show. */
export function splitDateTime(iso: string | null): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}
